import { useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { CONTENT_MAX_WIDTH } from '@/styles/layout';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { HomeData } from '@/pages/Home/Home';

const MobileFooterContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    max-width: ${CONTENT_MAX_WIDTH};
    margin: 0 auto;
    padding: 2rem 0;
  }
`;

const GridContainer = styled.div`
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;

const Card = styled.div`
  @media (max-width: 768px) {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
`;

const PostList = styled.div`
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }
`;

const PostListItem = styled.div`
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
    color: #374151;
    cursor: pointer;
    padding: 0.25rem 0;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f9fafb;
    }

    & > span:first-of-type {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const TagChip = styled.span`
  @media (max-width: 768px) {
    display: inline-block;
    padding: 0.313rem 0.5rem;
    font-size: 0.688rem;
    font-weight: 500;
    background-color: #eef2ff;
    color: #4b5563;
    border-radius: 9999px;
    margin-left: 0.75rem;
    flex-shrink: 0;
  }
`;

const CardTitle = styled.h3`
  @media (max-width: 768px) {
    font-size: 1.125rem;
    line-height: 1.5rem;
    font-weight: 700;
    margin-top: 0.25rem;
    margin-bottom: 0.5rem;
  }
`;

const SectionHeader = styled.div`
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.375rem;
  }
`;

const SeeAllLink = styled.a`
  @media (max-width: 768px) {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: #6b7280;
    text-decoration: none;
    display: flex;
    align-items: center;
    cursor: pointer;

    &:hover {
      color: #374151;
    }
  }
`;

const SeminarListContainer = styled.div`
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    flex: 1;
  }
`;

const SeminarListItem = styled.div`
  display: flex;
  gap: 0;
  padding: 0;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  background-color: white;
  min-height: 90px;
  height: 90px;
  align-items: stretch;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
`;

const SeminarItemThumbnailImg = styled.img`
  width: 90px;
  height: 100%;
  object-fit: cover;
  background: #f9fafb;
  flex-shrink: 0;
  border-radius: 0.75rem 0 0 0.75rem;
`;

const SeminarItemPlaceholder = styled.div`
  width: 90px;
  height: 100%;
  background: #f5f6f8;
  flex-shrink: 0;
  border-radius: 0.75rem 0 0 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #999;
  text-align: center;
  padding: 4px;
`;

const SeminarThumbnail = ({ url }: { url?: string }) => {
    const [error, setError] = useState(false);
    if (!url || error) {
        return <SeminarItemPlaceholder>이미지<br/>준비중</SeminarItemPlaceholder>;
    }
    return <SeminarItemThumbnailImg src={url} onError={() => setError(true)} />;
};

const SeminarItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
  padding: 0.625rem;
`;

const SeminarItemMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.25rem;
`;

const SeminarItemPrice = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #059669;
`;

const SubCardTitle = styled.h4`
  font-weight: 700;
  font-size: 0.85rem;
  margin: 0 0 0.15rem 0;
  line-height: 1.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubCardDescription = styled.p`
  font-size: 0.75rem;
  line-height: 1rem;
  color: #6b7280;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SeminarCarousel = styled(Slider)`
  .slick-dots {
    display: none !important;
  }

  .slick-prev, .slick-next {
    display: none !important;
  }
`;

const SeminarSlide = styled.div`
  display: flex !important;
  flex-direction: column;
  gap: 0.375rem;
`;

interface HomeMobileFooterProps {
    homeData: HomeData | null;
}

function HomeMobileFooter({ homeData }: HomeMobileFooterProps) {
    const navigate = useNavigate();

    // props로 받은 데이터 사용
    const hotPosts = homeData?.hotPosts || [];
    const todaySeminars = homeData?.todaySeminars || [];

    return (
        <MobileFooterContainer>
            <GridContainer>
                <Card>
                    <SectionHeader>
                        <CardTitle>지금 HOT🔥 글</CardTitle>
                        <SeeAllLink onClick={() => navigate('/posts')}><ChevronRightIcon /></SeeAllLink>
                    </SectionHeader>

                    <PostList>
                        {hotPosts.slice(0, 5).map((post, index) => (
                            <PostListItem key={post.id || index} onClick={() => navigate(`/posts/${post.id}`)}>
                                <span>{post.title}</span>
                                <TagChip>조회 {post.viewCount}</TagChip>
                            </PostListItem>
                        ))}
                        {hotPosts.length === 0 && (
                            <PostListItem style={{ cursor: 'default' }}>
                                <span>등록된 게시글이 없습니다.</span>
                            </PostListItem>
                        )}
                    </PostList>
                </Card>

                <Card>
                    <SectionHeader>
                        <CardTitle>오늘의 세미나!</CardTitle>
                        <SeeAllLink onClick={() => navigate('/seminars')}><ChevronRightIcon /></SeeAllLink>
                    </SectionHeader>

                    <SeminarListContainer>
                        {todaySeminars.length > 0 ? (
                            <SeminarCarousel
                                dots={false}
                                infinite={todaySeminars.length > 2}
                                speed={500}
                                slidesToShow={1}
                                slidesToScroll={1}
                                autoplay={todaySeminars.length > 2}
                                autoplaySpeed={7000}
                                arrows={false}
                            >
                                {Array.from({ length: Math.ceil(todaySeminars.length / 2) }).map((_, slideIndex) => (
                                    <SeminarSlide key={slideIndex}>
                                        {todaySeminars.slice(slideIndex * 2, slideIndex * 2 + 2).map((seminar) => (
                                            <SeminarListItem
                                                key={seminar.id}
                                                onClick={() => navigate(`/seminars/${seminar.id}`)}
                                            >
                                <SeminarThumbnail url={seminar.thumbnailUrl} />
                                                <SeminarItemContent>
                                                    <SubCardTitle>{seminar.title}</SubCardTitle>
                                                    <SubCardDescription>
                                                        {seminar.instructorNickname || seminar.instructor}
                                                    </SubCardDescription>
                                                    <SeminarItemMeta>
                                                        <SeminarItemPrice>
                                            {(seminar.participationFee ?? 0) === 0
                                                ? '무료'
                                                : `₩${(seminar.participationFee ?? 0).toLocaleString()}`}
                                                        </SeminarItemPrice>
                                                    </SeminarItemMeta>
                                                </SeminarItemContent>
                                            </SeminarListItem>
                                        ))}
                                    </SeminarSlide>
                                ))}
                            </SeminarCarousel>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                                등록된 세미나가 없습니다.
                            </div>
                        )}
                    </SeminarListContainer>
                </Card>
            </GridContainer>
        </MobileFooterContainer>
    );
}

export default HomeMobileFooter;
